import React, { useEffect, useState } from "react";
import { Panel, Field } from "@/pages/admin/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save } from "lucide-react";
import { toast } from "sonner";
import api from "@/pages/admin/api";

const TIMEZONES = ["UTC", "America/New_York", "America/Los_Angeles", "Europe/London", "Europe/Berlin", "Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo", "Australia/Sydney"];
const DATE_FORMATS = ["YYYY-MM-DD", "DD/MM/YYYY", "MM/DD/YYYY", "DD-MMM-YYYY"];
const LANGS = [["en", "English"], ["es", "Spanish"], ["fr", "French"], ["de", "German"], ["hi", "Hindi"], ["ar", "Arabic"]];

export default function GeneralSettings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/settings/general").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;

  const set = (k, v) => setData({ ...data, [k]: v });

  const save = async () => {
    setSaving(true);
    try { const r = await api.put("/settings/general", data); setData(r.data); toast.success("General settings updated"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6">
      <Panel title="Site Identity" actions={<Button data-testid="save-general" onClick={save} disabled={saving} className="rounded-xl bg-blue-600 hover:bg-blue-700 text-white"><Save size={14} className="mr-1" /> {saving ? "Saving..." : "Save"}</Button>}>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Website Name"><Input value={data.website_name || ""} onChange={(e) => set("website_name", e.target.value)} /></Field>
          <Field label="Website Email"><Input type="email" value={data.website_email || ""} onChange={(e) => set("website_email", e.target.value)} /></Field>
          <Field label="Timezone">
            <Select value={data.timezone} onValueChange={(v) => set("timezone", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TIMEZONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Language">
            <Select value={data.language} onValueChange={(v) => set("language", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{LANGS.map(([code, n]) => <SelectItem key={code} value={code}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Date Format">
            <Select value={data.date_format} onValueChange={(v) => set("date_format", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{DATE_FORMATS.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Currency"><Input value={data.currency || ""} onChange={(e) => set("currency", e.target.value)} /></Field>
        </div>
      </Panel>

      <Panel title="SMTP (Email delivery)">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="SMTP Host"><Input value={data.smtp_host || ""} onChange={(e) => set("smtp_host", e.target.value)} placeholder="smtp.gmail.com" /></Field>
          <Field label="SMTP Port"><Input type="number" value={data.smtp_port || 587} onChange={(e) => set("smtp_port", Number(e.target.value))} /></Field>
          <Field label="SMTP Username"><Input value={data.smtp_user || ""} onChange={(e) => set("smtp_user", e.target.value)} /></Field>
          <Field label="SMTP Password"><Input type="password" value={data.smtp_password || ""} onChange={(e) => set("smtp_password", e.target.value)} placeholder="•••••••" /></Field>
        </div>
      </Panel>
    </div>
  );
}
